// ** React Imports
import { useEffect, useState } from "react";

// ** Redux & Store & Actions
import { connect, useSelector } from "react-redux";
import { clearRegisterForm as clearRegisterFormAction } from "store/modules/auth";

// ** Third Party Components
import { toast } from "helpers/toastWrapper";
import { useTranslation } from "react-i18next";

// ** Custom Components
import ProgressBar from "../ProgressBar";
import StepLabel from "../StepLabel";
import Block from "components/layout/Block";
import Text from "components/typography/Text";
import Tooltip from "components/layout/Tooltip";
import { ButtonText } from "components/layout/Button/ButtonText";

// ** Hooks
import { useBlockResendEmail } from "../../hooks/useBlockResendEmail";

// ** Styled Components
import { StyledContentWrapper } from "containers/Landing/containers/Register/styles";

// ** Utils & Helpers
import api from "helpers/ApiClient";
import formatMessage from "utils/formatMessage";

// #####################################################

const StepThankYou = ({
	currentStep,
	totalSteps,
	clearRegisterForm,
	email,
	registrationId,
	setShowCancelOauthButton,
	setShowContinueWithOauthButton,
	setGoogleAuth,
	isModal = false,
}) => {
	const { t } = useTranslation("common", "landing");

	const [regId, setRegId] = useState(registrationId);
	const [showEmail, setShowEmail] = useState(email);
	const [initialTimeResend, setInitialTimeResend] = useState(
		new Date().getTime()
	);

	const [isSending, setIsSending] = useState(false);

	const { isResendDisabledByTime, remainingTimeString, remainingTimeNumber } =
		useBlockResendEmail({ initialTime: initialTimeResend });

	const sendEmail = () => {
		setIsSending(true);
		api.post("/authorization/registration/web/send", {
			id: regId,
			email: showEmail,
		})
			.then(({ data } = {}) => {
				if (!data) {
					setInitialTimeResend(new Date().getTime());
				} else {
					setInitialTimeResend(new Date(data?.send).getTime());
				}
				toast.success(t("landing:register.email.resendSuccessText"));
			})
			.catch((error) => {
				if (error?.response?.data?.error) {
					toast.error(`${error.response.data.error}`);
				}
			})
			.finally(() => {
				setIsSending(false);
			});
	};

	useEffect(() => {
		setRegId(registrationId);
		setShowEmail(email);
		clearRegisterForm();
		setShowCancelOauthButton(false);
		setShowContinueWithOauthButton(false);
		return () => {
			setGoogleAuth({
				token: null,
				state: null,
				active: false,
			});
		};
	}, []);

	// ** A/B Tests
	const authModalVersion = useSelector(
		(state) => state.globalCommon.authModal.version
	);

	// #####################################################

	return (
		<>
			<StyledContentWrapper>
				{/* Nagłówek */}
				<StepLabel
					label={t("landing:register.submitStepTwo")}
					isModal={isModal}
					authModalVersion={authModalVersion}
				/>
				<Block px={6} textCenter>
					<Text>
						{formatMessage(
							t("landing:register.registrationEmailStepTwo"),
							{
								emailAddres: (
									<Text
										bold
										key="landing:register.registrationEmailStepTwoemailAddres"
									>
										{showEmail}
									</Text>
								),
							}
						)}
					</Text>
				</Block>
				{regId && showEmail && (
					<Block flex mt={4} justifyCenter style={{ gap: "0.5rem" }}>
						<Block>
							<Text size="0.9rem">
								{t("landing:register.email.resendText")}
							</Text>
						</Block>
						<Tooltip
							text={
								isResendDisabledByTime &&
								remainingTimeString &&
								remainingTimeNumber > 0 &&
								`${t(
									"landing:register.email.resendTooltipText"
								)} ${remainingTimeString}`
							}
						>
							<ButtonText
								isLanding
								$isModal={isModal}
								onClick={() => {
									if (!isResendDisabledByTime && !isSending) {
										sendEmail();
									}
								}}
								disabled={isResendDisabledByTime || isSending}
							>
								{t("landing:register.email:resendLink")}
							</ButtonText>
						</Tooltip>
					</Block>
				)}
			</StyledContentWrapper>
			{/* Pasek Postępu */}
			<ProgressBar
				currentStep={currentStep}
				totalSteps={totalSteps}
				isModal={isModal}
				authModalVersion={authModalVersion}
			/>
		</>
	);
};

// #####################################################

const mapStateToProps = ({
	auth: {
		register: {
			registerForm: {
				email: { email },
				registrationId,
			},
		},
	},
}) => ({
	email,
	registrationId,
});

const mapDispatchToProps = (dispatch) => ({
	clearRegisterForm: () => dispatch(clearRegisterFormAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(StepThankYou);

// #####################################################
